import { VDRFolderView } from '../../../slices/marketplace/data-room/models/vdr-folder-view.model';
import { VDRFileView } from '../../../slices/marketplace/data-room/models/vdr-file-view.model';

export class VDRDocumentSelectionHelper {
    private rootFolder: VDRFolderView | undefined;
    private selectedPaths: Set<string>;

    constructor(rootFolder: VDRFolderView | undefined, selectedPaths: Set<string>) {
        this.rootFolder = rootFolder;
        this.selectedPaths = selectedPaths;
    }

    findFolderByPath(path: string, folder?: VDRFolderView): VDRFolderView | undefined {
        if (!folder) {
            folder = this.rootFolder;
        }
        if (!folder) {
            return undefined;
        }

        if (folder.fullPath === path) {
            return folder;
        }

        for (const subFolder of folder.subFolders) {
            const found = this.findFolderByPath(path, subFolder);
            if (found) {
                return found;
            }
        }

        return undefined;
    }

    findFileByUrl(fileUrl: string, folder?: VDRFolderView): VDRFileView | undefined {
        if (!folder) {
            folder = this.rootFolder;
        }
        if (!folder) {
            return undefined;
        }

        for (const file of folder.files) {
            if (file.fileUrl === fileUrl) {
                return file;
            }
        }

        for (const subFolder of folder.subFolders) {
            const found = this.findFileByUrl(fileUrl, subFolder);
            if (found) {
                return found;
            }
        }

        return undefined;
    }

    getAllChildrenPaths(folderPath: string): string[] {
        const folder = this.findFolderByPath(folderPath);
        if (!folder) {
            return [];
        }

        const paths: string[] = [];

        folder.subFolders.forEach((subFolder: VDRFolderView) => {
            paths.push(subFolder.fullPath);
            paths.push(...this.getAllChildrenPaths(subFolder.fullPath));
        });

        folder.files.forEach((file: VDRFileView) => {
            paths.push(file.fileUrl);
        });

        return paths;
    }

    getAllFilePathsInFolder(folderPath: string): string[] {
        const folder = this.findFolderByPath(folderPath);
        if (!folder) {
            return [];
        }

        const filePaths: string[] = [];

        folder.files.forEach((file: VDRFileView) => {
            filePaths.push(file.fileUrl);
        });

        folder.subFolders.forEach((subFolder: VDRFolderView) => {
            filePaths.push(...this.getAllFilePathsInFolder(subFolder.fullPath));
        });

        return filePaths;
    }

    private getAllFilesInFolderRecursive(folder: VDRFolderView): string[] {
        const filePaths: string[] = [];
        folder.files.forEach((file: VDRFileView) => {
            filePaths.push(file.fileUrl);
        });
        folder.subFolders.forEach((subFolder: VDRFolderView) => {
            filePaths.push(...this.getAllFilesInFolderRecursive(subFolder));
        });
        return filePaths;
    }

    getSelectedFiles(): VDRFileView[] {
        const files: VDRFileView[] = [];
        const processedFilePaths = new Set<string>();
        const folderPaths = new Set<string>();

        this.selectedPaths.forEach((path: string) => {
            const file = this.findFileByUrl(path);
            if (file && !processedFilePaths.has(path)) {
                files.push(file);
                processedFilePaths.add(path);
            } else {
                const folder = this.findFolderByPath(path);
                if (folder) {
                    folderPaths.add(path);
                }
            }
        });

        folderPaths.forEach((folderPath: string) => {
            const allFilePathsInFolder = this.getAllFilePathsInFolder(folderPath);
            allFilePathsInFolder.forEach((filePath: string) => {
                if (!processedFilePaths.has(filePath) && this.selectedPaths.has(filePath)) {
                    const folderFile = this.findFileByUrl(filePath);
                    if (folderFile) {
                        files.push(folderFile);
                        processedFilePaths.add(filePath);
                    }
                }
            });
        });

        return files;
    }

    getSelectedFilePaths(): string[] {
        const files = this.getSelectedFiles();
        return files.map((file: VDRFileView) => file.fileUrl);
    }

    getSelectionStateForFolder(folderPath: string): {
        checked: boolean;
        indeterminate: boolean;
    } {
        const folder = this.findFolderByPath(folderPath);
        if (!folder) {
            return { checked: false, indeterminate: false };
        }

        const allFilePathsInFolder = this.getAllFilesInFolderRecursive(folder);
        if (allFilePathsInFolder.length === 0) {
            if (this.selectedPaths.has(folderPath)) {
                return { checked: true, indeterminate: false };
            }
            return { checked: false, indeterminate: false };
        }

        const selectedFileCount = allFilePathsInFolder.filter((filePath: string) =>
            this.selectedPaths.has(filePath)
        ).length;

        const isFolderPathSelected = this.selectedPaths.has(folderPath);

        if (isFolderPathSelected) {
            const deselectedCount = allFilePathsInFolder.filter((filePath: string) =>
                !this.selectedPaths.has(filePath)
            ).length;
            if (deselectedCount === 0) {
                return { checked: true, indeterminate: false };
            } else {
                return { checked: false, indeterminate: true };
            }
        }

        if (selectedFileCount === 0) {
            return { checked: false, indeterminate: false };
        } else if (selectedFileCount === allFilePathsInFolder.length) {
            return { checked: true, indeterminate: false };
        } else {
            return { checked: false, indeterminate: true };
        }
    }

    handleFolderSelection(folderPath: string, isSelected: boolean): Set<string> {
        const newSelectedPaths = new Set(this.selectedPaths);

        if (isSelected) {
            newSelectedPaths.add(folderPath);
            const childrenPaths = this.getAllChildrenPaths(folderPath);
            childrenPaths.forEach((childPath: string) => {
                newSelectedPaths.add(childPath);
            });
        } else {
            newSelectedPaths.delete(folderPath);
            const childrenPaths = this.getAllChildrenPaths(folderPath);
            childrenPaths.forEach((childPath: string) => {
                newSelectedPaths.delete(childPath);
            });
        }

        return newSelectedPaths;
    }

    handleFileSelection(fileUrl: string, isSelected: boolean, parentFolderPath?: string): Set<string> {
        const newSelectedPaths = new Set(this.selectedPaths);

        if (isSelected) {
            newSelectedPaths.add(fileUrl);
        } else {
            newSelectedPaths.delete(fileUrl);
            if (parentFolderPath && newSelectedPaths.has(parentFolderPath)) {
                const folder = this.findFolderByPath(parentFolderPath);
                if (folder) {
                    const allFilePaths = this.getAllFilesInFolderRecursive(folder);
                    const hasSelectedFiles = allFilePaths.some((path) => newSelectedPaths.has(path));
                    if (!hasSelectedFiles) {
                        newSelectedPaths.delete(parentFolderPath);
                    }
                }
            }
        }

        return newSelectedPaths;
    }

    handleSelectAll(
        items: Array<{ fullPath: string; type: 'folder' | 'file' }>,
        isSelect: boolean,
        currentFolderPath?: string
    ): Set<string> {
        const newSelectedPaths = new Set(this.selectedPaths);

        if (isSelect) {
            items.forEach((item) => {
                if (item.type === 'folder') {
                    newSelectedPaths.add(item.fullPath);
                    const childrenPaths = this.getAllChildrenPaths(item.fullPath);
                    childrenPaths.forEach((childPath: string) => {
                        newSelectedPaths.add(childPath);
                    });
                } else {
                    newSelectedPaths.add(item.fullPath);
                }
            });
            
            if (currentFolderPath && items.length > 0) {
                const currentFolder = this.findFolderByPath(currentFolderPath);
                if (currentFolder) {
                    const allFilePaths = this.getAllFilesInFolderRecursive(currentFolder);
                    const allSubFolderPaths = currentFolder.subFolders.map((sf) => sf.fullPath);
                    const allChildrenPaths = this.getAllChildrenPaths(currentFolderPath);
                    
                    const allFilesSelected = allFilePaths.every((path) => newSelectedPaths.has(path));
                    const allSubFoldersSelected = allSubFolderPaths.every((path) => newSelectedPaths.has(path));
                    const allChildrenSelected = allChildrenPaths.every((path) => newSelectedPaths.has(path));
                    
                    if (allFilesSelected && allSubFoldersSelected && allChildrenSelected) {
                        newSelectedPaths.add(currentFolderPath);
                    }
                }
            }
        } else {
            items.forEach((item) => {
                if (item.type === 'folder') {
                    newSelectedPaths.delete(item.fullPath);
                    const childrenPaths = this.getAllChildrenPaths(item.fullPath);
                    childrenPaths.forEach((childPath: string) => {
                        newSelectedPaths.delete(childPath);
                    });
                } else {
                    newSelectedPaths.delete(item.fullPath);
                }
            });
            
            if (currentFolderPath) {
                newSelectedPaths.delete(currentFolderPath);
            }
        }

        return newSelectedPaths;
    }
}

